import { useCallback, useMemo, useRef } from 'react';
import { useTimelineValidation } from './useTimelineValidation';
import { transformCycles, shouldUseCurrentValue, updateExternalCycleTime } from '../utils/helpers/cycle-helpers';
import type { CycleCallback, ValidationError, CycleData } from '../types';

export interface TimelineActionsProps {
	cycles: CycleData[];
	handleSelectCycle: (cycleId: string | null) => void;
	handleUpdateCyclePosition: (cycleId: string, start: number, end: number) => void;
}

function hasOnlyOverlapErrors(errors: ValidationError[]): boolean {
	return errors.length > 0 && errors.every((error) => error.type === 'overlap');
}

function isUnjustifiedBoundsError(error: ValidationError, cycle?: CycleCallback): boolean {
	const result = error.type === 'bounds' && cycle?.is_justified === false;

	return result;
}

function haveSameCycleIds(current: CycleCallback[], previous: CycleCallback[]): boolean {
	if (current.length !== previous.length) {
		return false;
	}

	const previousIds = new Set(previous.map((cycle) => cycle.id));

	const result = current.every((cycle) => previousIds.has(cycle.id));

	return result;
}

export function useTimelineActions(
	value: CycleCallback[],
	onChange: (cycles: CycleCallback[]) => void,
	videoDuration: number,
	onValidationError?: (error: ValidationError | null) => void,
	onCycleSelect?: (cycleId: string | null) => void,
	allowBilateral = true,
	allCycles?: CycleCallback[],
	allowOverlap = false
): Readonly<TimelineActionsProps> {
	const previousHadErrorRef = useRef(false);
	const onValidationErrorRef = useRef(onValidationError);
	const previousValidValueRef = useRef<CycleCallback[]>(value);
	onValidationErrorRef.current = onValidationError;

	const { validateCycles } = useTimelineValidation(videoDuration, allowBilateral, allowOverlap);

	const cycles = useMemo(() => {
		const { isValid, errors } = validateCycles(value);

		if (isValid && previousHadErrorRef.current) {
			onValidationErrorRef.current?.(null);
			previousHadErrorRef.current = false;
		}

		if (isValid) {
			previousValidValueRef.current = value;

			return transformCycles(value);
		}

		const hasOnlyUnjustifiedBoundsErrors = errors.every((error) => {
			if (error.type !== 'bounds') {
				return false;
			}

			const cycle = value.find((c) => c.id === error.cycleId);
			const result = cycle?.is_justified === false;

			return result;
		});
		const canKeepInvalidOverlap = allowOverlap && hasOnlyOverlapErrors(errors);

		const cycleSetChanged = !haveSameCycleIds(value, previousValidValueRef.current);

		const useCurrentValue =
			shouldUseCurrentValue(errors, videoDuration) ||
			hasOnlyUnjustifiedBoundsErrors ||
			canKeepInvalidOverlap ||
			cycleSetChanged;

		let cyclesToUse = value;

		if (!useCurrentValue) {
			cyclesToUse = previousValidValueRef.current;
		}

		if (!allowOverlap) {
			errors.forEach((error) => {
				const cycle = value.find((c) => c.id === error.cycleId);

				if (isUnjustifiedBoundsError(error, cycle)) {
					return;
				}

				onValidationErrorRef.current?.(error);
			});
		}

		if (canKeepInvalidOverlap) {
			errors.forEach((error) => {
				onValidationErrorRef.current?.(error);
			});
			previousHadErrorRef.current = true;
		}

		return transformCycles(cyclesToUse);
	}, [value, videoDuration, validateCycles, allowOverlap]);

	const handleUpdateCyclePosition = useCallback(
		(cycleId: string, start: number, end: number) => {
			const cyclesForLookup = allCycles || value;
			const cycle = cyclesForLookup.find((c) => c.id === cycleId);

			if (!cycle) {
				return;
			}

			const updatedCycles = updateExternalCycleTime(value, cycleId, start, end, allCycles);

			const { isValid, errors } = validateCycles(updatedCycles);
			const canPersistWithOverlapErrors = allowOverlap && hasOnlyOverlapErrors(errors);

			if (isValid) {
				onValidationErrorRef.current?.(null);
			}

			if (!isValid) {
				errors.forEach((error) => {
					const reportedError =
						error.type === 'overlap' && error.conflictWith === cycleId
							? { ...error, cycleId, conflictWith: error.cycleId }
							: error;
					onValidationErrorRef.current?.(reportedError);
				});

				if (!canPersistWithOverlapErrors) {
					return;
				}
			}

			onChange(updatedCycles);
		},
		[value, onChange, validateCycles, allCycles, allowOverlap]
	);

	const handleSelectCycle = useCallback(
		(cycleId: string | null) => {
			onCycleSelect?.(cycleId);
		},
		[onCycleSelect]
	);

	return {
		cycles,
		handleUpdateCyclePosition,
		handleSelectCycle
	};
}
